import {
  Avatar,
  Breadcrumbs,
  Button,
  Card,
  CardHeader,
  Chip,
  Drawer,
  colors,
  FormControlLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
  Typography,
  Box,
  Paper,
  IconButton,
  Collapse,
  Divider,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import {
  BeforeAndAfterComparison,
  IconSetTypes,
} from "../transforms/components/BeforeAndAfterEventComparison";
import AddIcon from "@material-ui/icons/AddOutlined";
import SearchIcon from "@material-ui/icons/SearchOutlined";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
  FilterList,
  Timelapse,
  DateRange,
  HelpOutline,
  Mail,
  Inbox,
  CheckBoxOutlined,
  ErrorOutlineOutlined,
  PriorityHighOutlined,
  WarningOutlined,
} from "@material-ui/icons";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

import { makeStyles } from "@material-ui/core/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import "prismjs/components/prism-clike";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-javascript";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useRef,
  ReactElement,
} from "react";
import { useAlert } from "react-alert";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import Editor from "react-simple-code-editor";

import SelectEventDefinition, {
  EventDefOption,
} from "../components/Dropdown/SelectEventDefinition";
import useEnvironment from "../hooks/use-environment";
import { useAllSaasDestinations } from "../hooks/useAllSaasDestinations";
import Label from "../Label";
import Navigation from "../Navigation";
import { StoreState } from "../store";
import {
  LoadedDestinationInfo,
  DestinationConnectionMode,
} from "../types/global";
import { SET_EVENT_TESTER_DATA } from "../reducers/eventTester";
import { makeRequest, userIsFPEmployee, parseNestedJSON } from "../util";
import { DestinationSlug, IngestTraceRow, IPResponse } from "./types";
import queryString from "query-string";
import Select, { GroupedOptionsType, GroupType, ValueType } from "react-select";
import GroupBase from "react-select";
import OptionTypeBase from "react-select";
import ActionMeta from "react-select";
import DatetimeRangePicker from "react-datetime-range-picker";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import {
  slugForDestination,
  RequestableSaasDestination,
  SaaSDestination,
  warehouseDestinations,
  saasDestinations,
} from "./destinations";
import _ from "lodash";
import { TransformationEventType } from "../transforms/types";
import { AxiosError } from "axios";
import useArrayOfDestinations from "../hooks/useArrayOfDestinations";
import useDestinationById from "../hooks/useDestinationById";

type TraceStatusMetadata = {
  label: string; // The text to show users
  group: StatusGroup; // which group in selection boxes to place the element
  tooltip?: string; // tooltip to display on hover
};

enum StatusGroup {
  Multiple = "Groups of Multiple Statuses",
  Success = "Success Status",
  Warning = "Warning Status",
  Error = "Error Status",
}
// Which order to show groups in
const StatusGroupOrdering = [
  StatusGroup.Multiple,
  StatusGroup.Success,
  StatusGroup.Warning,
  StatusGroup.Error,
];

const statusMetadata = new Map<string, TraceStatusMetadata>([
  /*
    Helpers that select multiple statuses
  */
  [
    "selector_query_all_warnings",
    {
      label: "All Warnings",
      group: StatusGroup.Multiple,

      tooltip: "Include all events with a status categorized as a warning",
    },
  ],
  [
    "selector_query_all_errors",
    {
      label: "All Errors",
      group: StatusGroup.Multiple,

      tooltip: "Include all events with a status categorized as an error",
    },
  ],

  /*
  Success Results
  */
  [
    "ok",
    {
      label: "OK",
      group: StatusGroup.Success,

      tooltip:
        "This destination accepted our message and didn't report an error to us.",
    },
  ],
  [
    "transformation_skip",
    {
      label: "Skipped due to Transformation",
      group: StatusGroup.Success,

      tooltip: undefined,
    },
  ],
  [
    "dynamic_property_skip",
    {
      label: "Skipped due to property",
      group: StatusGroup.Success,

      tooltip: undefined,
    },
  ],

  /*
  Warnings
  */
  [
    "missing_required_property",
    {
      label: "Missing Required Property",
      group: StatusGroup.Warning,

      tooltip:
        "We did not send this request to the destination, because we determined that your payload is missing a required property.",
    },
  ],
  [
    "consent_not_granted",
    {
      label: "Consent Not Granted",
      group: StatusGroup.Success,

      tooltip:
        "We did not send this request to the destination because the user did not consent.",
    },
  ],

  /*
  Errors
  */
  [
    "unknown",
    {
      label: "Unknown Error",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "transformation_error",
    {
      label: "Transformation Error",
      group: StatusGroup.Error,

      tooltip:
        "There was an error applying the transformation rule for this event. Please review transformation rules for your events at **TransformationError:** “There was an error applying the transformation rule for this event. Please review transformation rules for your events (https://app.freshpaint.io/transformations).",
    },
  ],
  [
    "timeout",
    {
      label: "Timeout",
      group: StatusGroup.Error,

      tooltip:
        "We attempted to send the event to the destination, but the request to the destination timed out.",
    },
  ],
  [
    "freshpaint_internal_error",
    {
      label: "Freshpaint Internal Error",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "network_error",
    {
      label: "Network Error",
      group: StatusGroup.Error,

      tooltip:
        "We encountered an error when trying to connect to the destination.",
    },
  ],
  [
    "request_too_large",
    {
      label: "Request Too Large",
      group: StatusGroup.Error,

      tooltip:
        "The request is too large and was dropped because the server will not accept the request.",
    },
  ],
  [
    "malformed_property",
    {
      label: "Malformed Property",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "token_expired",
    {
      label: "Token Expired",
      group: StatusGroup.Error,

      tooltip:
        "The token configured for this destination has expired. Please confirm you have a valid, up-to-date token value for this destination.",
    },
  ],
  [
    "downstream_error",
    {
      label: "Downstream Error",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "ambigious_user",
    {
      label: "Ambiguous User",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "unauthorized_user",
    {
      label: "Unauthorized User",
      group: StatusGroup.Error,

      tooltip:
        "Your user account is not authorized to perform this action. Please reach out to your account administrator to confirm your permissions settings.",
    },
  ],
  [
    "invalid_event_def",
    {
      label: "Invalid Event Definition",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "invalid_transformer",
    {
      label: "Invalid Transformation",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "invalid_connection_mode_server_side",
    {
      label: "Invalid Connection Mode Server Side",
      group: StatusGroup.Error,

      tooltip:
        "This action can only be completed when the destination’s connection mode is set to Server-side.",
    },
  ],
  [
    "not_configured",
    {
      label: "Not Configured",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "invalid_event_payload",
    {
      label: "Invalid Event Payload",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],

  /*
  golang net/http/status.go all 3xx -> 5xx status codes
  */
  [
    "http_multiple_choices",
    {
      label: "Multiple Choices",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_moved_permanently",
    {
      label: "Moved Permanently",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  ["http_found", { label: "Found", group: StatusGroup.Error, tooltip: "" }],
  [
    "http_see_other",
    {
      label: "See Other",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_modified",
    {
      label: "Not Modified",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_use_proxy",
    {
      label: "Use Proxy",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_temporary_redirect",
    {
      label: "Temporary Redirect",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_permanent_redirect",
    {
      label: "Permanent Redirect",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_bad_request",
    {
      label: "Bad Request",
      group: StatusGroup.Error,

      tooltip:
        "We attempted to send the event to the destination, but the destination did not accept the HTTP request. Verify that your API key is correct.",
    },
  ],
  [
    "http_unauthorized",
    {
      label: "Unauthorized",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_payment_required",
    {
      label: "Payment Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_forbidden",
    {
      label: "Forbidden",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_found",
    {
      label: "Not Found",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_method_not_allowed",
    {
      label: "Method Not Allowed",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_acceptable",
    {
      label: "Not Acceptable",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_proxy_authentication_required",
    {
      label: "Proxy Authentication Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_request_timeout",
    {
      label: "Request Timeout",
      group: StatusGroup.Warning,

      tooltip: undefined,
    },
  ],
  [
    "http_conflict",
    {
      label: "Conflict",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  ["http_gone", { label: "Gone", group: StatusGroup.Error, tooltip: "" }],
  [
    "http_length_required",
    {
      label: "Length Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_precondition_failed",
    {
      label: "Precondition Failed",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_request_entity_too_large",
    {
      label: "Request Entity Too Large",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_request_uri_too_long",
    {
      label: "Request URI Too Long",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_unsupported_media_type",
    {
      label: "Unsupported Media Type",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_requested_range_not_satisfiable",
    {
      label: "Requested Range Not Satisfiable",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_expectation_failed",
    {
      label: "Expectation Failed",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_im_a_teapot",
    {
      label: "I'm a teapot",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_misdirected_request",
    {
      label: "Misdirected Request",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_unprocessable_entity",
    {
      label: "Unprocessable Entity",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_locked",
    {
      label: "Locked",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_failed_dependency",
    {
      label: "Failed Dependency",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_too_early",
    {
      label: "Too Early",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_upgrade_required",
    {
      label: "Upgrade Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_precondition_required",
    {
      label: "Precondition Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_too_many_requests",
    {
      label: "Too Many Requests",
      group: StatusGroup.Warning,

      tooltip: undefined,
    },
  ],
  [
    "http_request_header_fields_too_large",
    {
      label: "Request Header Fields Too Large",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_unavailable_for_legal_reasons",
    {
      label: "Unavailable For Legal Reasons",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_internal_server_error",
    {
      label: "Internal Server Error",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_implemented",
    {
      label: "Not Implemented",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_bad_gateway",
    {
      label: "Bad Gateway",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_service_unavailable",
    {
      label: "Service Unavailable",
      group: StatusGroup.Warning,

      tooltip: undefined,
    },
  ],
  [
    "http_gateway_timeout",
    {
      label: "Gateway Timeout",
      group: StatusGroup.Warning,

      tooltip: undefined,
    },
  ],
  [
    "http_http_version_not_supported",
    {
      label: "HTTP Version Not Supported",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_variant_also_negotiates",
    {
      label: "Variant Also Negotiates",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_insufficient_storage",
    {
      label: "Insufficient Storage",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_loop_detected",
    {
      label: "Loop Detected",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_extended",
    {
      label: "Not Extended",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_network_authentication_required",
    {
      label: "Network Authentication Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
]);

type TraceStepMetadata = {
  label: string; // The text to show users
  tooltip?: string; // tooltip to display on hover
  explanation?: string; // This is the message we display within a trace.
};

const stepMetadata = new Map<string, TraceStepMetadata>([
  [
    "start_ingest",
    {
      label: "Start",
      tooltip:
        "This is the event we received from the source and we're processing it",
      explanation: "We received an event and started processing it.",
    },
  ],
  [
    "hipaa_guard",
    {
      label: "Allow List",
      tooltip:
        "The properties that are allowed by the allow list to be sent to the destination",
      explanation:
        "We applied your allow list and may have removed some fields from the payload.",
    },
  ],
  [
    "anon_ip",
    {
      label: "Anonymize IP",
      tooltip:
        "We attempted to find an IP address within the same state to use for geo-location",
      explanation:
        "We replaced the IP address with an address that can't be used to trace the user.",
    },
  ],
  [
    "bad_parameter",
    {
      label: "Bad Parameter",
      tooltip: undefined,
      explanation: "We encountered an issue with a Property.",
    },
  ],
  [
    "consent_decision",
    {
      label: "Consent Decision",
      tooltip: undefined,
      explanation:
        "Checking whether the user has provided consent for sending data to a destination.",
    },
  ],
  [
    "delivery_result",
    {
      label: "Result",
      tooltip: undefined,
      explanation: "We finished processing the event.",
    },
  ],
  [
    "http",
    {
      label: "HTTP",
      tooltip:
        "We made a web request to the destination and received a response",
      explanation: "We made an HTTP request to the destination",
    },
  ],
  [
    "reenqueue",
    {
      label: "Queued for Retry",
      tooltip: undefined,
      explanation:
        "We've sent the event to our retry queue to try again later.",
    },
  ],
  [
    "send_enhanced_measurement",
    {
      label: "Sending Event",
      tooltip: "Passing event to the destination",
      explanation: "We attempted to send this event to the destination",
    },
  ],
  [
    "send_group",
    {
      label: "Sending Event",
      tooltip: "Passing event to the destination",
      explanation: "We attempted to send this event to the destination",
    },
  ],
  [
    "send_identify",
    {
      label: "Sending Event",
      tooltip: "Passing event to the destination",
      explanation: "We attempted to send this event to the destination",
    },
  ],
  [
    "send_page",
    {
      label: "Sending Event",
      tooltip: "Passing event to the destination",
      explanation: "We attempted to send this event to the destination",
    },
  ],
  [
    "send_track",
    {
      label: "Sending Event",
      tooltip: "Passing event to the destination",
      explanation: "We attempted to send this event to the destination",
    },
  ],
  [
    "transformation",
    {
      label: "Transformation",
      tooltip: undefined,
      explanation: "We applied a transformation to the original event.",
    },
  ],
  [
    "select_relevant_properties",
    {
      label: "Destination Properties",
      tooltip: "Shows the properties that the destination will use.",
      explanation: "These are the properties that the destination uses.",
    },
  ],
  [
    "debug_log",
    {
      label: "Troubleshooting Info",
      tooltip: "Shows supplemental information for debugging purposes",
      explanation: "We logged additional information for debugging purposes.",
    },
  ],
]);

const filterStatusOptions: GroupedOptionsType<SelectStringOption> = (() => {
  var result: GroupedOptionsType<SelectStringOption> = [];

  StatusGroupOrdering.forEach((x) => {
    let options: SelectStringOption[] = [];

    statusMetadata.forEach((value, key) => {
      if (value.group === x) {
        options.push({
          value: key,
          label: (
            <>
              <Tooltip title={value.tooltip || ""}>
                <div>{value.label}</div>
              </Tooltip>
            </>
          ),
        });
      }
    });

    result = result.concat({
      label: x,
      options: options,
    });
  });

  return result;
})();

type SelectStringOption = { label: string | ReactElement; value: string };

function DestinationSelect({
  onChange,
  locked,
  initialDestination,
  destinations,
  allSaasDestinations,
}: {
  onChange: any;
  locked?: boolean;
  initialDestination?: any;
  destinations: LoadedDestinationInfo[];
  allSaasDestinations: {
    [destSlug: string]:
      | SaaSDestination
      | RequestableSaasDestination
      | undefined;
  };
}) {
  const classes = useStyles();

  let getLogo = (dest) => {
    const destinationSlug = slugForDestination(dest);
    return allSaasDestinations[destinationSlug]?.logoSquare;
  };

  // warehouse destinations will not produce traces
  let warehouseFilter = (value) => {
    return (
      _.find(warehouseDestinations, (v) => {
        return v?.key === value.name;
      }) === undefined
    );
  };

  // disabled destinations may contain valid traces, but we don't want to show
  // a disabled destination as a choice for weeks after a destination has been disabled.
  // for now we won't show them at all, but ideally we'd do better and show the option
  // if there is recent trace data only.
  let disabledDestinationsFilter = (value) => {
    return value.enabled === true;
  };

  destinations = destinations
    .filter(warehouseFilter)
    .filter(disabledDestinationsFilter)
    .sort((a, b) => a.name.localeCompare(b.name));

  let renderOption = (dest) => {
    const matchingDest = allSaasDestinations[dest.type_slug] || null;
    let name = matchingDest?.name || dest.name;
    let DestinationIcon = getLogo(dest);
    return (
      <React.Fragment>
        {DestinationIcon && (
          <DestinationIcon
            className={clsx(classes.destinationIcon)}
            height={34}
            width={34}
          />
        )}
        <div>
          {name}
          {matchingDest instanceof SaaSDestination &&
            matchingDest?.supportsMultiConfig && (
              <>
                <br />
                <small>
                  {matchingDest.multiConfigLabel}: {dest.instance_name}
                </small>
              </>
            )}
        </div>
      </React.Fragment>
    );
  };

  return (
    <Autocomplete
      key={initialDestination}
      disabled={locked}
      style={{ width: 300 }}
      options={destinations}
      value={initialDestination}
      autoHighlight
      getOptionLabel={(dest) => {
        /*
          dest.name is not necessarily a customer facing name for a destination.
          So find the customer facing name from allSaasDestinations.
        */
        const matchingDest = allSaasDestinations[dest.type_slug] || null;
        let name = matchingDest?.name || dest.name;
        if (
          matchingDest instanceof SaaSDestination &&
          matchingDest?.supportsMultiConfig
        ) {
          name += `\n${matchingDest.multiConfigLabel}: ${dest.instance_name}`;
        }
        return name;
      }}
      onChange={onChange}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Choose a Destination"
          variant="outlined"
          multiline={true}
        />
      )}
    />
  );
}

function DestinationTraceListViewer({
  initialDest,
  destinations,
  allSaasDestinations,
}) {
  const alert = useAlert();
  const classes = useStyles();
  const chipClasses = useChipStyles();

  const [selectedEventDef, setSelectedEventDef] = useState<
    EventDefOption | null | undefined
  >(null);

  const [backendFilters, setBackendFilters] = useState<BackendFilters>({
    date_rel_duration: "4h",
    date_type: "duration",
  } as BackendFilters);

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [openHelp, setOpenHelp] = useState<boolean>(false);

  const [traces, setTraces] = useState<[IngestTraceRow] | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterDateType, setFilterDateType] = useState("duration");
  const [filterDateEnd, setFilterDateEnd] = useState("");
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterOnlyMe, setFilterOnlyMe] = useState(false);
  const [filterString, setFilterString] = useState("");

  const debounceFilterStringLike = useDebounce(() => {
    setBackendFilters({
      ...backendFilters,
      search_string: filterString,
    });
  });

  const [selectedDest, setDest] = useState();
  let currentDest = selectedDest || initialDest;
  let destination = allSaasDestinations[currentDest?.type_slug] || undefined;

  function refresh() {
    setTraces(null);
    setRefreshKey((oldKey) => oldKey + 1);
  }

  useEffect(() => {
    if (currentDest) {
      setTraces(null);

      makeRequest<{ traces: [IngestTraceRow] }>("list_destination_traces", {
        dest_id: currentDest.id,
        filters: backendFilters,
      })
        .then((response) => {
          setTraces(response.data.traces);
        })
        .catch((error) => {
          alert.error("Something went wrong. Unable to fetch traces.");
          console.log(error);
          Sentry.captureException(error);
        });
    } else {
      setTraces(null);
    }
  }, [currentDest, refreshKey, backendFilters]);

  var loading = currentDest && !traces;

  let onChangeDestination = (event, newVal) => setDest(newVal);

  let isClientSide =
    currentDest !== undefined && currentDest.connection_mode !== "server";

  const [removeSamplingDialogOpen, setRemoveSamplingDialogOpen] = useState(
    false
  );

  return (
    <Card className={clsx(classes.card)}>
      <CardHeader
        title={
          <div
            className={clsx(
              classes.cardHeader,
              classes.cardHeaderSuppressBorder
            )}
          >
            <div className={clsx(classes.cardTitle)}>Event Verification</div>
            {initialDest ? (
              <DestinationSelect
                initialDestination={initialDest}
                locked={true}
                onChange={onChangeDestination}
                destinations={destinations}
                allSaasDestinations={allSaasDestinations}
              />
            ) : (
              <DestinationSelect
                onChange={onChangeDestination}
                destinations={destinations}
                allSaasDestinations={allSaasDestinations}
              />
            )}
            {isClientSide && (
              <>
                &nbsp;
                <div>
                  <Tooltip title="This destination is in client-mode, event traces will not capture any events sent directly from a client or browser to the destination.">
                    <Chip
                      className={chipClasses["warning"]}
                      size="small"
                      label="client-mode"
                    />
                  </Tooltip>
                </div>
              </>
            )}
          </div>
        }
      />
      <Box className={clsx(classes.helpText)}>
        <Typography variant="body1">
          View an event as Freshpaint received it, the transformations made to
          it, and the exact HTTP calls made to send the event to the
          destination.
          <br />
          Note: Traces can take up to 90 seconds to appear from when Freshpaint
          receives the event and only consist of a sample of events.
        </Typography>
      </Box>

      <div className={clsx(classes.flex, classes.filters)}>
        <ToggleButtonGroup
          className={clsx(classes.cardHeader, classes.filtersName)}
          orientation="horizontal"
          value={filterDateType}
          exclusive
          onChange={(event, newAlignment) => {
            setFilterDateType(newAlignment);
            setBackendFilters({
              ...backendFilters,
              date_type: newAlignment,
            });
          }}
        >
          <ToggleButton value="duration" aria-label="list">
            <Timelapse />
          </ToggleButton>
          <ToggleButton value="timerange" aria-label="module">
            <DateRange />
          </ToggleButton>
        </ToggleButtonGroup>

        {filterDateType === "duration" && (
          <ToggleButtonGroup
            className={clsx(classes.filters, classes.flexGrow)}
            value={backendFilters?.date_rel_duration}
            exclusive
            onChange={(event, newAlignment) =>
              setBackendFilters({
                ...backendFilters,
                date_rel_duration: newAlignment,
              })
            }
            aria-label="text alignment"
          >
            <ToggleButton value="1h">1h</ToggleButton>
            <ToggleButton value="4h">4h</ToggleButton>
            <ToggleButton value="6h">6h</ToggleButton>
            <ToggleButton value="12h">12h</ToggleButton>
            <ToggleButton value="24h">1d</ToggleButton>
            <ToggleButton value="48h">2d</ToggleButton>
            <ToggleButton value="120h">5d</ToggleButton>
            <ToggleButton value="168h">7d</ToggleButton>
          </ToggleButtonGroup>
        )}

        {filterDateType === "timerange" && (
          <DatetimeRangePicker
            className={clsx(classes.filters, classes.flexGrow)}
            timeFormat="hh:mm a"
            dateFormat="MM/DD"
            startDate={filterDateStart}
            endDate={filterDateEnd}
            inline={false}
            input={false}
            onChange={(x) => {
              setFilterDateEnd(x.end);
              setFilterDateStart(x.start);
              setBackendFilters({
                ...backendFilters,
                date_start: Math.round(x.start.getTime() / 1000),
                date_end: Math.round(x.end.getTime() / 1000),
              });
            }}
          />
        )}

        <ToggleButtonGroup
          className={clsx(classes.cardHeader)}
          orientation="horizontal"
        >
          <RemoveSamplingModal
            removeSamplingDialogOpen={removeSamplingDialogOpen}
            setRemoveSamplingDialogOpen={setRemoveSamplingDialogOpen}
          ></RemoveSamplingModal>

          <Tooltip title="Filter traces to only those that originate from the IP address of your machine">
            <ToggleButton
              value="check"
              selected={filterOnlyMe}
              onChange={(x) => {
                setFilterOnlyMe(!filterOnlyMe);

                setBackendFilters({
                  ...backendFilters,
                  only_me: !filterOnlyMe,
                });
              }}
            >
              Show only me
            </ToggleButton>
          </Tooltip>

          <ToggleButton
            value="check"
            selected={false}
            onClick={() => refresh()}
          >
            Refresh
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <div className={clsx(classes.flex)}>
        <SelectEventDefinition
          value={selectedEventDef}
          prependOptions={freshpaintSystemEventOptions}
          onChange={(x) => {
            setSelectedEventDef(x);

            if (Object.keys(x).length === 0 && x.constructor === Object) {
              setBackendFilters({
                ...backendFilters,
                types: [],
                event_def_id: undefined,
              });
            } else if (x?.value?.id === emptyEventDefID) {
              setBackendFilters({
                ...backendFilters,
                types: [x?.value?.name],
              });
            } else {
              setBackendFilters({
                ...backendFilters,
                event_def_id: x?.value?.id,
              });
            }
          }}
          className={clsx(classes.filters, classes.filterDropdown)}
          isClearable={true}
          placeholder="Event Definition"
        />

        <SelectStatus
          className={clsx(classes.filters, classes.filterDropdown)}
          setFilter={(x) =>
            setBackendFilters({
              ...backendFilters,
              statuses: x?.map((x) => x.value),
            })
          }
        />

        <div style={{ flexGrow: 1 }}></div>
        <Box style={{ paddingTop: 20 }}>
          <Typography>{traces ? traces.length : 0} event(s)</Typography>
        </Box>
        <TextField
          size="small"
          className={clsx(classes.searchField)}
          fullWidth
          variant="outlined"
          autoComplete="off"
          name="search"
          onChange={(e) => {
            setFilterString(e.target.value);
            // indicate that we're loading when typing
            setTraces(null);
            debounceFilterStringLike();
          }}
          value={filterString}
          InputProps={{
            placeholder: "Search Trace Contents",
            style: { fontSize: "16px", font: "inherit" },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={clsx(classes.icon)} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {loading && <pre style={{ paddingLeft: 15 }}>Loading...</pre>}

      <Table style={{ tableLayout: "auto" }}>
        <TableHead>
          <TableRow>
            <TableCell>Time</TableCell>
            <TableCell>Event</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>View</TableCell>
          </TableRow>
        </TableHead>
        {traces && traces.every((trace) => trace) && (
          <TableBody>
            {/* We filter out the `integrations_object_skipped` status, because
             that status doesn't provide meaningful information for debugging.
             Also, this status is expected for GTM integrations, and it is just
             noise for this use case.
             */}
            {traces
              .filter((trace) => trace.Status !== "integrations_object_skipped")
              .map((trace, i) => (
                <TraceRow
                  key={i}
                  trace={trace}
                  destination={destination}
                  configuredDest={currentDest}
                />
              ))}
          </TableBody>
        )}
      </Table>
    </Card>
  );
}

function RemoveSamplingModal({
  removeSamplingDialogOpen,
  setRemoveSamplingDialogOpen,
}) {
  const alert = useAlert();
  const [removeSamplingDialogIP, setRemoveSamplingDialogIP] = useState("");

  const closeRemoveSamplingDialog = () => {
    setRemoveSamplingDialogOpen(false);
  };

  function openRemoveSamplingDialog() {
    makeRequest<{ ip: string }>("get_ip", {}).then((response) => {
      var ip = response.data.ip;
      if (removeSamplingDialogIP === "") {
        setRemoveSamplingDialogIP(ip);
      }
    });
  }

  function saveRemoveSamplingDialog() {
    try {
      makeRequest<{}>("trace_unsample_ip", {
        ip: removeSamplingDialogIP,
      }).then((response) => {
        closeRemoveSamplingDialog();
        alert.success(
          "Sampling has been removed for IP address: " + removeSamplingDialogIP
        );
      });
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status === 403) {
        alert.error("You do not have permission to perform this action.");
      } else {
        alert.error("Failed to remove sampling.");
      }
      console.log(error);
      Sentry.captureException(error);
    }
  }

  return (
    <>
      <Dialog
        open={removeSamplingDialogOpen}
        onClose={closeRemoveSamplingDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Remove Sampling from an IP address"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            By default, we do not store every event - we only store a sample of
            events. Sampling can be disabled for 12 hours by specifying an IP
            address below. Your IP address is shown by default.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="ip_address"
            label="IP Address"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={removeSamplingDialogIP}
            onChange={(x) => {
              setRemoveSamplingDialogIP(x.target.value);
            }}
            inputProps={{
              pattern:
                "^((d{1,2}|1dd|2[0-4]d|25[0-5]).){3}(d{1,2}|1dd|2[0-4]d|25[0-5])$",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveSamplingDialog}>Cancel</Button>
          <Button onClick={saveRemoveSamplingDialog} autoFocus>
            Remove Sampling
          </Button>
        </DialogActions>
      </Dialog>
      <Tooltip title="Useful when debugging. Normally we don't store every trace; use this if you want to capture a trace for every event coming from your IP address.">
        <ToggleButton
          value="check"
          selected={removeSamplingDialogOpen}
          onChange={(x) => {
            openRemoveSamplingDialog();
            setRemoveSamplingDialogOpen(!removeSamplingDialogOpen);
          }}
        >
          Remove Sampling
        </ToggleButton>
      </Tooltip>
    </>
  );
}

interface BackendFilters {
  types?: string[];
  event_def_id?: string;
  stream_names?: string[];
  statuses?: string[];
  date_type?: string;
  date_rel_duration?: string;
  date_start?: number;
  date_end?: number;
  only_me?: boolean /* The API server will use the source-ip of the API request to filter events to the client IP address */;
  search_string?: string;
}

interface SelectStringProps<Option, IsMulti, Group> {
  className?: string;
  value?: ValueType<SelectStringOption>;
  setFilter: (option: SelectStringOption[] | null | undefined) => void;
  isLoading?: boolean;
}

function SelectStatus<
  Option extends OptionTypeBase,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(props: SelectStringProps<Option, IsMulti, Group>) {
  return (
    <Select
      isMulti
      menuPosition="fixed"
      value={props.value}
      onChange={(value) => {
        if (value == null) {
          props?.setFilter(null);
        } else if (Array.isArray(value)) {
          props?.setFilter(value);
        } else {
          props?.setFilter([value as SelectStringOption]);
        }
      }}
      options={filterStatusOptions}
      className={props.className}
      isLoading={props.isLoading}
      isSearchable={true}
      placeholder="Status"
    />
  );
}

const useChipStyles = makeStyles((theme) => {
  return {
    success: {
      borderRadius: "5px",
      borderWidth: "2px",
      borderStyle: "solid",
      color: theme.palette.success.dark,
      backgroundColor: theme.palette.success.subtle,
      borderColor: theme.palette.success.bright,
    },
    error: {
      borderRadius: "5px",
      borderWidth: "2px",
      borderStyle: "solid",
      color: theme.palette.error.dark,
      backgroundColor: theme.palette.error.subtle,
      borderColor: theme.palette.error.bright,
    },
    warning: {
      borderRadius: "5px",
      borderWidth: "2px",
      borderStyle: "solid",
      color: theme.palette.warning.dark,
      backgroundColor: theme.palette.warning.subtle,
      borderColor: theme.palette.warning.bright,
    },
  };
});

const useIconStyles = makeStyles((theme) => {
  return {
    success: {
      color: theme.palette.success.dark,
      borderColor: theme.palette.success.bright,
    },
    error: {
      color: theme.palette.error.dark,
      borderColor: theme.palette.error.bright,
    },
    warning: {
      color: theme.palette.warning.dark,
      borderColor: theme.palette.warning.bright,
    },
  };
});

function StatusChip({ status, label }: { label?: string; status: string }) {
  const classes = useChipStyles();
  var statusType = "error";
  var metaDataLabel;

  let tooltip =
    "Cannot find help for this error. Please talk to your Freshpaint representative for more help debugging.";
  if (
    statusMetadata.has(status) &&
    typeof statusMetadata.get(status) !== undefined
  ) {
    var meta = statusMetadata.get(status);
    tooltip = meta?.tooltip || tooltip;

    if (meta?.group === StatusGroup.Success) {
      statusType = "success";
    }
    if (meta?.group === StatusGroup.Warning) {
      statusType = "warning";
    }

    metaDataLabel = meta?.label;
  }

  return (
    <Tooltip title={tooltip}>
      <Chip
        className={classes[statusType]}
        size="small"
        label={label || metaDataLabel || status}
      />
    </Tooltip>
  );
}

function StatusIcon({ status, label }: { label?: string; status: string }) {
  const classes = useIconStyles();
  var statusType = "error";
  var metaDataLabel;

  let tooltip =
    "Cannot find help for this error. Please talk to your Freshpaint representative for more help debugging.";
  if (
    statusMetadata.has(status) &&
    typeof statusMetadata.get(status) !== undefined
  ) {
    var meta = statusMetadata.get(status);
    tooltip = meta?.tooltip || tooltip;

    if (meta?.group === StatusGroup.Success) {
      statusType = "success";
    }
    if (meta?.group === StatusGroup.Warning) {
      statusType = "warning";
    }

    metaDataLabel = meta?.label;
  }

  function renderSwitch() {
    switch (statusType) {
      case "warning":
        return <WarningOutlined className={classes[statusType]} />;
      case "success":
        return <CheckBoxOutlined className={classes[statusType]} />;
      default:
        return <ErrorOutlineOutlined className={classes[statusType]} />;
    }
  }

  return <Tooltip title={tooltip}>{renderSwitch()}</Tooltip>;
}

const eventDefinitionTooltips = {
  user_defined_event: "This event was defined in your Event library.",
  system_event:
    "This event is a system event that is collected and processed automatically by Freshpaint.",
};

// TODO CJ I don't believe this is ever called without a destination
function TraceRow({
  trace,
  destination,
  configuredDest,
}: {
  trace: IngestTraceRow;
  destination: SaaSDestination | undefined;
  configuredDest: LoadedDestinationInfo | undefined;
}) {
  let date = new Date(trace.Time * 1000).toString();

  let redirectPath = "/event-verification/" + trace.ID;
  if (destination && configuredDest) {
    redirectPath = `${destination.configPageUrl()}/${
      configuredDest.id
    }/event-verification/${trace.ID}`;
  }

  let eventCategory = trace.EventDefName
    ? "user_defined_event"
    : "system_event";

  return (
    <TableRow className="hover:bg-gray-100">
      <TableCell>
        <pre className="text-xs break-words whitespace-pre-wrap">{date}</pre>
      </TableCell>
      <TableCell>
        <Tooltip title={eventDefinitionTooltips[eventCategory]}>
          <pre className="text-xs break-words whitespace-pre-wrap">
            {trace.EventDefName ? (
              <a href={"/events/schema/" + trace.EventDefID}>
                {trace.EventDefName}
              </a>
            ) : (
              trace.EventType
            )}
          </pre>
        </Tooltip>
      </TableCell>
      <TableCell>{<StatusChip status={trace.Status} />}</TableCell>
      <TableCell>
        <a href={redirectPath} target="_blank">
          <Button color="primary" variant="outlined">
            View
          </Button>
        </a>
      </TableCell>
    </TableRow>
  );
}

const drawerWidth = 250;

const useStyles = makeStyles(() => ({
  navigation: {
    paddingTop: 12,
  },
  card: {
    minHeight: "50vw",
  },
  lightText: {
    color: "#546E7A",
  },
  darkText: {
    color: "#263238",
  },
  header: {
    "font-size": "14pt",
    "font-weight": "bold",
    color: "#263238",
  },
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
  },
  cardHeaderOpen: {
    paddingRight: 350,
  },
  cardHeaderBottomBorder: {
    borderBottom: "1px solid #eeeeee",
  },
  headerDetails: {
    fontSize: "12pt",
    fontWeight: "normal",
  },
  cardTitle: {
    paddingRight: 15,
  },
  flexGrow: {
    flexGrow: 1,
  },
  addFilterButton: {
    marginRight: 15,
  },
  applyFilterButton: {
    height: 34,
  },
  icon: {
    display: "flex",
    verticalAlign: "middle",
  },
  filters: {
    padding: 10,
    verticalAlign: "middle",
  },
  filtersGrid: {
    paddingLeft: 20,
    padding: 20,
  },
  flex: {
    display: "flex",
  },
  filtersName: {
    width: 120,
  },
  filterDropdown: {
    width: 400,
    fontSize: "16px",
    fontFamily: "Roboto, Helvetica, Arial, serif",
  },
  helpText: {
    color: "#444444",
    padding: 0,
    marginLeft: 24,
  },
  searchField: {
    width: 350,
    horizontalAlign: "middle",
    alignItems: "flex-end",
    padding: 10,
    fontFamily: "Roboto, Helvetica, Arial, serif",
    fontSize: "16px",

    "& .MuiInputBase-input::placeholder": {
      color: "#818181",
    },
  },
  searchWrapper: {},
  eventDefLink: {
    "&:hover": {
      "text-decoration": "underline",
    },
  },
  destinationIcon: {
    marginRight: 20,
  },
  drawerPaper: {
    width: 100,
  },
  drawer: {
    position: "fixed",
    //marginLeft: "auto",
    width: drawerWidth,
    "& .MuiBackdrop-root": {
      display: "none",
    },
    "& .MuiDrawer-paper": {
      width: drawerWidth,
      position: "absolute",
      height: "calc(100vh - 200px)", //I'm not exactly sure where the extra 200 pixels is commin from
      transition: "none !important",
    },
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    // necessary for content to be below app bar
    justifyContent: "flex-start",
  },
  traceStepHeader: {
    "& .MuiTypography-root": {
      fontSize: "12pt",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "24px",
    },
  },
  traceText: {
    fontSize: "18pt",
  },
  traceBody: {
    height: "calc(100vh - 210px)",
  },
  traceSteps: {
    overflowY: "scroll",
    overflowX: "hidden",
    height: "calc(100vh - 200px)",
    "margin-left": drawerWidth,
  },
  traceViewMarginRight: {
    "margin-right": 8,
  },
  traceDrawerHighlight: {
    "border-radius": 8,
    backgroundColor: "#3F51B51A",
    "& .MuiTypography-root": {
      color: "#3F51B5",
      fontWeight: 700,
    },
  },
  traceDrawerNormal: {
    "& .MuiTypography-root": {
      fontWeight: 400,
    },
  },
  traceEventBox: {
    padding: "0px 12px 0px 48px",
    position: "relative",
  },
  traceEventContent: {
    border: "1px solid",
    "border-radius": 8,
    "border-color": "#00000033",
    "font-size": "12pt",
    overflowX: "scroll",
  },
  traceAvatarHighlight: {
    color: "#F4F6F8",
    backgroundColor: "#3F51B5",
    width: 30,
    height: 30,
    fontSize: "12pt",
  },
  traceAvatarNoHighlight: {
    color: "#444444",
    backgroundColor: "#E1E2E2",
    width: 30,
    height: 30,
    fontSize: "12pt",
    fontWeight: 500,
  },
  cardHeaderSuppressBorder: {
    "padding-bottom": 0,
  },
}));

const DestinationTraceListViewerWrapper: React.FC<{
  destinationSlug: DestinationSlug;
  destinationId: string;
}> = ({ destinationSlug, destinationId }) => {
  const history = useHistory();
  const classes = useStyles();

  const allSaasDestinations = useAllSaasDestinations();

  const destination = useMemo(() => allSaasDestinations[destinationSlug], [
    allSaasDestinations,
    destinationSlug,
  ]);
  const category = useMemo(() => destination?.categoryDisplay(), [destination]);
  const goToCategory = useCallback(
    () => history.push(`/destinations/${category}`),
    [category, history]
  );

  const goToDestination = useCallback(
    () =>
      history.push(
        `/destinations/${category}/${destinationSlug}/${destinationId}`
      ),
    [category, history, destinationSlug, destinationId]
  );

  const destinations = useArrayOfDestinations();
  const configuredDest = destinationId
    ? useDestinationById(destinationId)
    : undefined;

  const uriPathIncludesDestination = !!destinationId;

  return (
    <Navigation className={clsx(classes.navigation)}>
      {uriPathIncludesDestination ? (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={clsx(classes.breadcrumb)}
        >
          <Link color="inherit" onClick={() => history.push(`/destinations`)}>
            <Typography className={clsx(classes.lightText)} variant="overline">
              Destinations
            </Typography>
          </Link>
          <Link color="inherit" onClick={goToCategory}>
            <Typography className={clsx(classes.lightText)} variant="overline">
              {destination?.categoryDisplay().toUpperCase()}
            </Typography>
          </Link>
          <Link color="inherit" onClick={goToDestination}>
            <Typography className={clsx(classes.lightText)} variant="overline">
              {destination?.name.toUpperCase()}
            </Typography>
          </Link>
          <Typography className={clsx(classes.darkText)} variant="overline">
            Event Verification
          </Typography>
        </Breadcrumbs>
      ) : (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={clsx(classes.breadcrumb)}
        >
          <Typography className={clsx(classes.darkText)} variant="overline">
            Event Verification
          </Typography>
        </Breadcrumbs>
      )}
      <DestinationTraceListViewer
        initialDest={configuredDest}
        destinations={destinations}
        allSaasDestinations={allSaasDestinations}
      />
    </Navigation>
  );
};

function DestinationTraceViewer({ traceId, destinationSlug, destinationId }) {
  const alert = useAlert();
  const [trace, setTrace] = useState<TraceLog[] | string | null>();

  useEffect(() => {
    makeRequest<any>("view_destination_trace", {
      ID: traceId,
    })
      .then((response) => {
        setTrace(response.data);
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to fetch requested trace.");
        setTrace("Something went wrong. Unable to fetch requested trace.");
        console.log(error);
        Sentry.captureException(error);
      });
  }, []);

  return trace ? (
    <DestinationTraceCard
      trace={trace}
      destinationSlug={destinationSlug}
      destinationId={destinationId}
      traceId={traceId}
    />
  ) : (
    <Card>Loading...</Card>
  );
}

type TraceLog = {
  Timestamp: number;
  Type: string;
  Values: { [key: string]: any };
};

function TraceHeader({ trace, traceId }) {
  const startIngest = trace.find((t) => t.Type === "start_ingest");
  const eventDef = trace.find((t) => t.Type === "event_definition");
  const finalEvent = trace.slice(-1)[0];
  const finalStatus = Object.keys(
    finalEvent.Values.delivery_result.DeliveryStatuses
  )[0];

  const classes = useStyles();
  var date = new Date(startIngest.Timestamp);

  return (
    <div>
      <span className={clsx(classes.header)}>Event Processing History</span>
      <div className={clsx(classes.headerDetails)}>
        <br />
        <span>
          <span>{date.toString()}</span>&nbsp;&mdash;&nbsp;
          {eventDef ? (
            <a
              className={clsx(classes.eventDefLink)}
              href={"/events/schema/" + eventDef.Values.event_def_id}
              target="_blank"
            >
              {eventDef.Values.event_def_name}
            </a>
          ) : (
            <>
              <Tooltip title={eventDefinitionTooltips["system_event"]}>
                <span>{startIngest.Values.event.event}</span>
              </Tooltip>
            </>
          )}
          &mdash;&nbsp;
          {<StatusChip status={finalStatus} />}
        </span>
        <br />
        {finalEvent?.Values?.delivery_result?.Metadata?.issue && (
          <>
            <br />
            {
              <StatusChip
                status={finalStatus}
                label={finalEvent.Values.delivery_result.Metadata.issue}
              />
            }
            <br />
          </>
        )}
      </div>
    </div>
  );
}

function DestinationTraceCard({
  trace,
  traceId,
  destinationSlug,
  destinationId,
}: {
  trace: any;
  traceId: string;
  destinationSlug: string;
  destinationId: string;
}): JSX.Element {
  const classes = useStyles();
  let body = JSON.stringify(trace, null, 2);

  const eventDef = trace.find((row) => row.Type === "event_definition");
  const eventDefID = eventDef?.Values?.event_def_id;
  if (typeof trace === "string") {
    return (
      <Card>
        <CardHeader title={<TraceHeader trace={trace} traceId={traceId} />} />
        <pre className="text-xs break-words whitespace-pre-wrap">{body}</pre>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        className={clsx(classes.cardHeader, classes.cardHeaderBottomBorder)}
        title={<TraceHeader trace={trace} traceId={traceId} />}
      />
      {/* The `event_definition` trace type does not need to be shown in the
       * Traces view, because it is already shown in the header */}
      <Tracetable
        rows={trace.filter((t) => t.Type !== "event_definition")}
        eventDefID={eventDefID}
        destinationSlug={destinationSlug}
        destinationId={destinationId}
      />
    </Card>
  );
}

function DiffRow(props: {
  row: TraceLog;
  eventDefID: string;
  destinationSlug: string;
  destinationId: string;
}) {
  const { row, eventDefID, destinationSlug } = props;
  const classes = useStyles();

  // The BeforeAfterDiff component requires us to pass in a `renamedProperties`
  // field. For the purposes of the `hipaa_guard`, we are not renaming properties
  // - only subtracting, so the best way to use the `BeforeAfterDiff` component
  // is to pass in all of the keys from the before diff that weren't deleted.
  // Passing in all keys says "I didn't plan to rename any keys, so any modified
  // or missing keys should be visualized as a diff"
  let renamedProps = {};
  for (const prop in row.Values.before) {
    if (row.Values.after[prop]) {
      renamedProps[prop] = prop;
    }
  }

  return (
    <Box sx={{ margin: 1 }} className={clsx(classes.traceEventBox)}>
      <div id="trace-row">
        <BeforeAndAfterComparison
          before={{
            eventName: row.Type,
            eventProperties: row.Values.before,
          }}
          after={{
            eventName: row.Type,
            eventProperties: row.Values.after,
            renamedProperties: renamedProps,
          }}
          eventType={row.Type as TransformationEventType}
          showEllipsis={false}
          iconSet={IconSetTypes.Security}
        />
      </div>
    </Box>
  );
}

function BasicRow(props: {
  row: TraceLog;
  eventDefID: string;
  destinationSlug: string;
  destinationId: string;
}) {
  const { row, eventDefID, destinationSlug, destinationId } = props;
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  let fullyParsedEvent = parseNestedJSON(row.Values);
  let event = JSON.stringify(fullyParsedEvent, null, 2);

  return (
    <div className={clsx(classes.traceEventBox)}>
      <Box className={clsx(classes.traceEventContent)}>
        <Box sx={{ position: "absolute", right: "30px", top: "10px" }}>
          {eventDefID && row.Type == "start_ingest" && (
            <Link
              color="inherit"
              onClick={() => {
                dispatch({
                  type: SET_EVENT_TESTER_DATA,
                  payload: {
                    event: row.Values.event.properties,
                    eventDefID: eventDefID,
                  },
                });
                history.push(
                  "/destinations/apps/" +
                    destinationSlug +
                    "/event-tester/" +
                    destinationId +
                    "?eventDefID=" +
                    eventDefID,
                  { from: "trace-details-view" }
                );
              }}
            >
              <Button color="primary" variant="outlined">
                Open in Event Tester
              </Button>
            </Link>
          )}
        </Box>
        <div id="trace-row">
          <pre>{event}</pre>
        </div>
      </Box>
    </div>
  );
}

function TraceStep(props: {
  row: TraceLog;
  eventDefID: string;
  destinationSlug: string;
  destinationId: string;
}) {
  const { row, eventDefID, destinationSlug, destinationId } = props;

  // All RowRenderers should take { row, destinationSlug, eventDefID } as
  // properties
  let RowRenderer;

  switch (row.Type) {
    case "select_relevant_properties":
    case "hipaa_guard":
      RowRenderer = DiffRow;
      break;
    default:
      RowRenderer = BasicRow;
      break;
  }

  return (
    <Collapse in={true} timeout="auto" unmountOnExit>
      <RowRenderer
        row={row}
        destinationSlug={destinationSlug}
        destinationId={destinationId}
        eventDefID={eventDefID}
      />
    </Collapse>
  );
}

// Because of how the Backend is implemented, we will receive
// traces for the following entries out-of-order.
//
// Thus, we need to reconstruct the correct order in the Frontend.
// Lower numbers come first.
const rowOrdering = {
  select_relevant_properties: 2,
  hipaa_guard: 3,
  http: 4,
};

function orderRows(row1: TraceLog, row2: TraceLog) {
  let r1 = rowOrdering[row1.Type];
  let r2 = rowOrdering[row2.Type];

  if (r1 && r2) {
    return r1 - r2;
  } else {
    // If row1 and row2 aren't both in the list defined above, then we don't want to
    // change their order and we can leave it as is, using 0 as a return.
    return 0;
  }
}

function Tracetable({
  rows,
  eventDefID,
  destinationSlug,
  destinationId,
}: {
  rows: TraceLog[];
  eventDefID: string;
  destinationSlug: string;
  destinationId: string;
}): JSX.Element {
  const classes = useStyles();
  const containerRef = useRef();
  const mainContentRef = useRef(null);

  const stepRefs: React.MutableRefObject<HTMLDivElement | null>[] = [];
  rows.forEach((row) => stepRefs.push(useRef(null)));

  //
  // scroll tracking to identify and highlight the active row in the viewport
  //
  // This took awhile to figure out as the basic implementation would often skip showing rows as active.
  // 1. We use the intersection observer as a fast way to identify which rows in the trace are currently visible on screen.
  // 2. The intersection observer only tells us when rows come into or leave view.
  //    So the first render might see multiple rows, and then we have to track that ourselves
  // 3. To do so, we maintain our own ordering of the sections from top to bottom, so that we always select the top most section to highlight.
  const [activeSection, setActiveSection] = useState("");
  const visibleSections = useRef(new Map());
  const [sectionOrder, setSectionOrder] = useState([""]);
  const observer: React.MutableRefObject<IntersectionObserver | null> = useRef(
    null
  );
  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibleSections.current[entry.target.id] = entry;
        });

        var visible = sectionOrder.find(
          (entry) => visibleSections.current[entry]?.isIntersecting
        );

        if (visible) {
          setActiveSection(visible);
        }
      },
      {
        root: mainContentRef.current, // this is the reference we use for viewport calculations
        threshold: 0, // 0 means only consider vertical visibility
        rootMargin: "0px",
      }
    );

    if (observer.current === null) return;

    const sections = document.querySelectorAll(
      'div[data-custom-property="trace-step-header"]'
    );
    var sectionIDs: string[] = [];

    sections.forEach((section) => {
      if (observer.current === null) return;
      observer.current.observe(section);
      sectionIDs.push(section.id);
    });
    setSectionOrder(sectionIDs);

    return () => {
      sections.forEach((section) => {
        if (observer.current === null) return;
        observer.current.unobserve(section);
      });
    };
  }, [rows]);

  function isRowActive(row: number) {
    return "trace-log-" + row.toString() === activeSection;
  }

  return (
    <div className={clsx(classes.traceBody)} ref={mainContentRef}>
      <Drawer
        ref={containerRef}
        className={clsx(classes.drawer)}
        variant="persistent"
        anchor="left"
        open={true}
      >
        <List>
          {rows.map((row, index) => {
            let rowText = stepMetadata.get(row.Type)?.label || row.Type;
            let rowTooltip = stepMetadata.get(row.Type)?.tooltip || "";

            let chip;
            if (row.Type == "delivery_result") {
              chip =
                Object.keys(row.Values?.delivery_result?.DeliveryStatuses)[0] ||
                null;
            }

            return (
              <ListItem
                button
                className={
                  isRowActive(index)
                    ? clsx(classes.traceDrawerHighlight)
                    : clsx(classes.traceDrawerNormal)
                }
                key={index}
                onClick={() => {
                  stepRefs[index].current?.scrollIntoView({
                    behavior: "smooth",
                  });
                }}
              >
                <div className={clsx(classes.traceViewMarginRight)}>
                  <Avatar
                    className={
                      isRowActive(index)
                        ? clsx(classes.traceAvatarHighlight)
                        : clsx(classes.traceAvatarNoHighlight)
                    }
                  >
                    {index + 1}
                  </Avatar>
                </div>
                <Tooltip title={rowTooltip}>
                  <ListItemText
                    primary={rowText}
                    classes={{ primary: classes.traceStepHeader }}
                  />
                </Tooltip>
                {chip && <StatusIcon status={chip} />}
              </ListItem>
            );
          })}
        </List>
      </Drawer>
      <div className={clsx(classes.traceSteps)}>
        <Box
          sx={{
            paddingLeft: 5,
          }}
          component="main"
        >
          {rows.sort(orderRows).map((row, index) => (
            <div key={index}>
              <ListItem button ref={stepRefs[index]}>
                <div
                  className={clsx(classes.traceViewMarginRight)}
                  data-custom-property="trace-step-header"
                  id={"trace-log-" + index.toString()}
                >
                  <Avatar
                    className={
                      isRowActive(index)
                        ? clsx(classes.traceAvatarHighlight)
                        : clsx(classes.traceAvatarNoHighlight)
                    }
                  >
                    {index + 1}
                  </Avatar>
                </div>
                <ListItemText
                  primary={stepMetadata.get(row.Type)?.explanation || row.Type}
                  className={clsx(classes.traceStepHeader)}
                />
                {row.Type === "delivery_result" && (
                  <StatusChip
                    status={
                      Object.keys(
                        row.Values.delivery_result.DeliveryStatuses
                      )[0]
                    }
                  />
                )}
              </ListItem>
              <TraceStep
                row={row}
                eventDefID={eventDefID}
                destinationSlug={destinationSlug}
                destinationId={destinationId}
              />
              <br />
            </div>
          ))}
        </Box>
      </div>
    </div>
  );
}

const DestinationTraceViewerWrapper: React.FC<{
  destinationSlug: DestinationSlug;
  destinationId: string;
  traceId: string;
}> = ({ destinationSlug, destinationId, traceId }) => {
  const history = useHistory();
  const classes = useStyles();

  const allSaasDestinations = useAllSaasDestinations();

  const destination = useMemo(() => allSaasDestinations[destinationSlug], [
    allSaasDestinations,
    destinationSlug,
  ]);
  const category = useMemo(() => destination?.categoryDisplay(), [destination]);
  const goToCategory = useCallback(
    () => history.push(`/destinations/${category}`),
    [category, history]
  );

  const goToDestination = useCallback(
    () =>
      history.push(
        `/destinations/${category}/${destinationSlug}/${destinationId}`
      ),
    [category, history, destinationSlug, destinationId]
  );

  const goToTraces = useCallback(
    () =>
      history.push(
        `/destinations/${category}/${destinationSlug}/${destinationId}/event-verification`
      ),
    [category, history, destinationSlug, destinationId]
  );

  return (
    <Navigation className={clsx(classes.navigation)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        className={clsx(classes.breadcrumb)}
      >
        <Link color="inherit" onClick={() => history.push(`/destinations`)}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            Destinations
          </Typography>
        </Link>
        <Link color="inherit" onClick={goToCategory}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            {destination?.categoryDisplay().toUpperCase()}
          </Typography>
        </Link>
        <Link color="inherit" onClick={goToDestination}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            {destination?.name.toUpperCase()}
          </Typography>
        </Link>
        <Link color="inherit" onClick={goToTraces}>
          <Typography className={clsx(classes.lightText)} variant="overline">
            Event Verification
          </Typography>
        </Link>
        <Typography className={clsx(classes.darkText)} variant="overline">
          {traceId}
        </Typography>
      </Breadcrumbs>
      <DestinationTraceViewer
        traceId={traceId}
        destinationSlug={destinationSlug}
        destinationId={destinationId}
      />
    </Navigation>
  );
};

const emptyEventDefID = "00000000-0000-0000-0000-000000000000";

const freshpaintSystemEventOptions: Array<GroupType<EventDefOption>> = [
  {
    label: "Freshpaint System",
    options: [
      {
        label: "Web Event",
        value: {
          id: emptyEventDefID,
          name: "$web_event",
          description: "",
          enabled_dests: {},
          enabled_client_destinations: {},
          enabled_server_destinations: {},
          segment_compatible: false,
          source: "server",
          type: "server",
          is_hidden: false,
          filter: {
            type: "nop",
            filters: [],
          },
          tags: {},
          snapshots: [],
        },
      },
      {
        label: "Identify",
        value: {
          id: emptyEventDefID,
          name: "$identify",
          description: "",
          enabled_dests: {},
          enabled_client_destinations: {},
          enabled_server_destinations: {},
          segment_compatible: false,
          source: "server",
          type: "server",
          is_hidden: false,
          filter: {
            type: "nop",
            filters: [],
          },
          tags: {},
          snapshots: [],
        },
      },
      {
        label: "Group",
        value: {
          id: emptyEventDefID,
          name: "$group",
          description: "",
          enabled_dests: {},
          enabled_client_destinations: {},
          enabled_server_destinations: {},
          segment_compatible: false,
          source: "server",
          type: "server",
          is_hidden: false,
          filter: {
            type: "nop",
            filters: [],
          },
          tags: {},
          snapshots: [],
        },
      },
      {
        label: "User Engagement",
        value: {
          id: emptyEventDefID,
          name: "$user_engagement",
          description: "",
          enabled_dests: {},
          enabled_client_destinations: {},
          enabled_server_destinations: {},
          segment_compatible: false,
          source: "server",
          type: "server",
          is_hidden: false,
          filter: {
            type: "nop",
            filters: [],
          },
          tags: {},
          snapshots: [],
        },
      },
      {
        label: "Scroll",
        value: {
          id: emptyEventDefID,
          name: "$scroll",
          description: "",
          enabled_dests: {},
          enabled_client_destinations: {},
          enabled_server_destinations: {},
          segment_compatible: false,
          source: "server",
          type: "server",
          is_hidden: false,
          filter: {
            type: "nop",
            filters: [],
          },
          tags: {},
          snapshots: [],
        },
      },
    ],
  },
];

export { DestinationTraceListViewerWrapper, DestinationTraceViewerWrapper };

// https://www.developerway.com/posts/debouncing-in-react
const useDebounce = (callback) => {
  const ref = useRef<Function>();

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  const debouncedCallback = useMemo(() => {
    const func = () => {
      ref.current?.();
    };

    return _.debounce(func, 1000);
  }, []);

  return debouncedCallback;
};
